import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../utils/api';

export interface SettingsData {
  aiAnalysisMode: 'existing' | 'advanced';
  ocrEnabled: boolean;
  multiModalAnalysis: boolean;
  confidenceThreshold: number;
  // Threat level configurations
  alertThreatLevel: 'medium' | 'high' | 'critical';
  analysisThreatLevel: 'low' | 'medium' | 'high' | 'critical';
  // URL analysis depth configuration
  analysisDepth: number;
  // AI Provider configuration
  aiProvider: 'anthropic' | 'openai' | 'grok' | 'gemini' | 'cloudflare';
  aiModel: string;
  // AI Fallback Provider configuration
  aiFallbackProvider: 'anthropic' | 'openai' | 'grok' | 'gemini' | 'cloudflare' | 'mistral' | 'deepseek' | 'none';
  aiFallbackModel: string;
  // Relevance AI Provider configuration
  relevanceAiProvider: 'anthropic' | 'openai' | 'grok' | 'gemini' | 'cloudflare' | 'mistral' | 'deepseek';
  relevanceAiModel: string;
  // Relevance Fallback Provider configuration
  relevanceFallbackProvider: 'anthropic' | 'openai' | 'grok' | 'gemini' | 'cloudflare' | 'none';
  relevanceFallbackModel: string;
  // Enhanced AI configuration
  enhancedAiEnabled: boolean;
  enhancedAiTrigger: 'always' | 'medium_threats_and_above' | 'high_threats' | 'critical_only' | 'never';
  enhancedAiProvider: 'anthropic' | 'openai' | 'grok' | 'gemini' | 'cloudflare';
  enhancedAiModel: string;
  enhancedAiFallbackProvider: 'anthropic' | 'openai' | 'grok' | 'gemini' | 'cloudflare' | 'none';
  enhancedAiFallbackModel: string;
  // AI Analysis Performance configuration
  aiAnalysisConcurrency: number;
  // Advanced Analysis Models (configurable)
  advancedTextProvider: 'anthropic' | 'openai' | 'grok' | 'gemini' | 'cloudflare' | 'mistral' | 'deepseek' | 'zai';
  advancedTextModel?: string;
  advancedOcrProvider: 'anthropic' | 'openai' | 'grok' | 'gemini' | 'cloudflare' | 'mistral' | 'deepseek' | 'zai';
  advancedOcrModel?: string;
  advancedVisionProvider: 'anthropic' | 'openai' | 'grok' | 'gemini' | 'cloudflare' | 'mistral' | 'deepseek' | 'zai';
  advancedVisionModel?: string;
  // R2 Storage configuration
  r2RetentionDays: number;
  postsRetentionDays: number;
  criticalRetentionDays: number;
  logsRetentionDays: number;
}

export const DEFAULT_SETTINGS: SettingsData = {
  aiAnalysisMode: 'existing',
  ocrEnabled: false,
  multiModalAnalysis: false,
  confidenceThreshold: 0.7,
  alertThreatLevel: 'high',
  analysisThreatLevel: 'high', // BUGFIX: Changed from 'low' to match backend DEFAULT_SETTINGS
  analysisDepth: 1,
  aiProvider: 'cloudflare',
  aiModel: '@cf/meta/llama-4-scout-17b-16e-instruct',
  aiFallbackProvider: 'anthropic',
  aiFallbackModel: 'claude-3-5-sonnet-20241022',
  relevanceAiProvider: 'anthropic',
  relevanceAiModel: 'claude-3-5-sonnet-20241022',
  relevanceFallbackProvider: 'cloudflare',
  relevanceFallbackModel: '@cf/meta/llama-4-scout-17b-16e-instruct',
  enhancedAiEnabled: false,
  enhancedAiTrigger: 'high_threats',
  enhancedAiProvider: 'anthropic',
  enhancedAiModel: 'claude-3-5-sonnet-20241022',
  enhancedAiFallbackProvider: 'none',
  enhancedAiFallbackModel: '',
  aiAnalysisConcurrency: 20,
  advancedTextProvider: 'cloudflare',
  advancedTextModel: '@cf/meta/llama-3.1-8b-instruct',
  advancedOcrProvider: 'cloudflare',
  advancedOcrModel: '@cf/mistral/pixtral-12b-2409',
  advancedVisionProvider: 'cloudflare',
  advancedVisionModel: '@cf/microsoft/resnet-50',
  l2AiEnabled: false,
  l2TextProvider: 'cloudflare',
  l2TextModel: '@cf/meta/llama-3.1-8b-instruct',
  l3AiEnabled: true,
  l3TextProvider: 'cloudflare',
  l3TextModel: '@cf/meta/llama-3.1-8b-instruct',
  r2RetentionDays: 30,
  postsRetentionDays: 30,
  criticalRetentionDays: 90,
  logsRetentionDays: 30
};

interface SettingsContextType {
  settings: SettingsData;
  updateSettings: (newSettings: Partial<SettingsData>) => void;
  saveSettings: () => Promise<void>;
  loadSettings: () => Promise<void>;
  resetSettings: () => void;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

interface SettingsProviderProps {
  children: ReactNode;
}

export function SettingsProvider({ children }: SettingsProviderProps) {
  const [settings, setSettings] = useState<SettingsData>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Load from localStorage first
      const savedSettings = localStorage.getItem('osm-threat-intel-settings');
      if (savedSettings) {
        try {
          const parsed = JSON.parse(savedSettings);
          setSettings({ ...DEFAULT_SETTINGS, ...parsed });
        } catch (parseError) {
          console.error('Failed to parse saved settings:', parseError);
          // Reset to defaults if parsing fails
          setSettings(DEFAULT_SETTINGS);
        }
      }

      // Try to load from backend API
      try {
        const response = await api.getSettings();
        console.log('[SettingsContext] API response:', response);

        // Merge with defaults but preserve saved values
        const mergedSettings = { ...DEFAULT_SETTINGS, ...response };
        console.log('[SettingsContext] Merged settings:', mergedSettings);
        console.log('[SettingsContext] aiFallbackProvider:', mergedSettings.aiFallbackProvider);

        setSettings(mergedSettings);

        // Update localStorage with backend settings
        localStorage.setItem('osm-threat-intel-settings', JSON.stringify(mergedSettings));
      } catch (apiError) {
        console.warn('Failed to load settings from API, using localStorage:', apiError);
        // Continue with localStorage settings - this is expected if backend doesn't have settings endpoint yet
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
      setError('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  const updateSettings = (newSettings: Partial<SettingsData>) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };

  const saveSettings = async () => {
    try {
      setIsSaving(true);
      setError(null);

      // Save to localStorage
      localStorage.setItem('osm-threat-intel-settings', JSON.stringify(settings));
      console.log('[SettingsContext] Saved to localStorage:', settings);
      console.log('[SettingsContext] aiFallbackProvider saved:', settings.aiFallbackProvider);

      // Save to backend API
      try {
        await api.saveSettings(settings);
        console.log('[SettingsContext] Saved to API successfully');
      } catch (apiError) {
        console.warn('Failed to save settings to API, but localStorage saved:', apiError);
        // Don't throw error here - localStorage save is sufficient for now
      }

    } catch (error) {
      console.error('Failed to save settings:', error);
      setError('Failed to save settings');
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const resetSettings = () => {
    setSettings(DEFAULT_SETTINGS);
    setError(null);
  };

  const value: SettingsContextType = {
    settings,
    updateSettings,
    saveSettings,
    loadSettings,
    resetSettings,
    isLoading,
    isSaving,
    error
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings(): SettingsContextType {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}